import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Editor } from '@tiptap/react';
import { GET_ALL } from '../../../../dictionary/dictionary.graphql';
import { useFeatureFlags } from '../../../../unleash';
import { DictionaryWord } from '../../tiptap/extensions/plugins/DictionaryHighlight';
import { matchableWord } from '../../../../dictionary/search';

/**
 * Feeds the dictionary's words into the editor (DictionaryHighlight): the begrep as `accepted`, and
 * its synonyms as `rejected` (nei-ord), since a guide should use the begrep. It runs outside the
 * sidebar so the nei-ord badge works while the sidebar is closed. Clicking an underlined word calls
 * `onTermClick` with its term id.
 */
export const useDictionarySync = (editor: Editor | null, onTermClick: (termId: string) => void) => {
  const { dictionary } = useFeatureFlags();
  const { data } = useQuery(GET_ALL, { skip: !dictionary || !editor });

  useEffect(() => {
    if (!editor || editor.isDestroyed || !data?.terms) return;
    const words: DictionaryWord[] = data.terms.flatMap((t) => {
      if (!t?.preferred) return [];
      const entry = (word: string, kind: DictionaryWord['kind']): DictionaryWord => ({ word, kind, preferred: t.preferred!, termId: t.id });
      return [entry(matchableWord(t.preferred), 'accepted'), ...(t.synonyms ?? []).flatMap((s) => (s ? [entry(s, 'rejected')] : []))];
    });
    editor.commands.setDictionaryWords(words);
  }, [editor, data]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const dom = editor.view.dom;
    const handleClick = (e: MouseEvent) => {
      const termId = (e.target as HTMLElement).closest<HTMLElement>('.rejected-term, .dictionary-term')?.dataset.termId;
      if (termId) onTermClick(termId);
    };
    dom.addEventListener('click', handleClick);
    return () => dom.removeEventListener('click', handleClick);
  }, [editor, onTermClick]);
};
